import { useTranslation } from 'react-i18next'

interface NextCallBannerProps {
  nextCall: {
    scheduledTime: number
    timeSlot?: string
    status: string
    retryCount: number
    medications: { name: string }[]
  } | null
}

/**
 * Banner showing the next scheduled medication reminder call
 */
export function NextCallBanner({ nextCall }: NextCallBannerProps) {
  const { t } = useTranslation()

  const bannerStyles: React.CSSProperties = {
    backgroundColor: '#f0f9ff',
    border: '1px solid #bae6fd',
    borderRadius: '12px',
    padding: '1rem',
    marginBottom: '1.5rem',
  }

  const labelStyles: React.CSSProperties = {
    fontSize: '12px',
    fontWeight: 500,
    color: '#0369a1',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: '0.25rem',
  }

  const timeStyles: React.CSSProperties = {
    fontSize: '16px',
    fontWeight: 600,
    color: '#0c4a6e',
  }

  const statusStyles: React.CSSProperties = {
    fontSize: '14px',
    color: '#0369a1',
    marginTop: '0.25rem',
  }

  const formatTime = (timestamp: number): string => {
    const date = new Date(timestamp)
    const now = new Date()
    const isToday = date.toDateString() === now.toDateString()
    const isTomorrow = date.toDateString() === new Date(now.getTime() + 86400000).toDateString()

    const timeStr = date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })

    if (isToday) {
      return `Today at ${timeStr}`
    } else if (isTomorrow) {
      return `Tomorrow at ${timeStr}`
    } else {
      return (
        date.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' }) +
        ` at ${timeStr}`
      )
    }
  }

  const getStatusText = (status: string, retryCount: number, timeSlot?: string): string => {
    switch (status) {
      case 'pending':
      case 'scheduled':
        return timeSlot ? `(${timeSlot.charAt(0).toUpperCase() + timeSlot.slice(1)})` : ''
      case 'retry_pending':
        return `(Retry ${retryCount} of 2 for ${timeSlot || 'medication'})`
      case 'initiated':
      case 'in_progress':
        return 'Calling now...'
      default:
        return ''
    }
  }

  if (!nextCall) {
    return (
      <div style={{ ...bannerStyles, backgroundColor: '#f0fdf4', borderColor: '#bbf7d0' }}>
        <div style={{ ...labelStyles, color: '#15803d' }}>
          {t('medications.nextCall', 'Next Call')}
        </div>
        <div style={{ ...timeStyles, color: '#166534' }}>
          {t('medications.allDoneToday', 'All reminders complete for today')} ✓
        </div>
      </div>
    )
  }

  const medicationNames = nextCall.medications.map(m => m.name).join(', ') || 'medication'

  return (
    <div style={bannerStyles}>
      <div style={labelStyles}>{t('medications.nextCall', 'Next Call')}</div>
      <div style={timeStyles}>{formatTime(nextCall.scheduledTime)}</div>
      <div style={statusStyles}>
        {medicationNames} {getStatusText(nextCall.status, nextCall.retryCount, nextCall.timeSlot)}
      </div>
    </div>
  )
}
